import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer,
    Spin,
    Tag,
    Card,
    Collapse,
    Table,
    Button,
    Space,
    Typography,
    Empty,
    Statistic,
    Progress,
    Alert,
    Segmented,
    Tooltip,
    Tabs,
} from 'antd';
import {
    DownloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    ClockCircleOutlined,
    UserOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type {
    EvalRunResultsResponse,
    EvalRunStatus,
    EvalPerQuestionResult,
    ConsistencyMetrics,
    ConsistencyScoreStat,
    ConsistencyPerTaskSection,
    TaskResultSqlOutput,
} from './evalTypes';
import {
    gateDisplayGroups,
    gateKeyMatchesPrefix,
    targetAgentMatchesPrefixes,
    targetAgentMatchesGroup,
    compareAgents,
    type GateDisplayGroup,
} from './evalAgentOrder';
import SkillStudioVizPreview from './SkillStudioVizPreview';

const { Text, Title } = Typography;

const statusConfig: Record<EvalRunStatus, { color: string; label: string }> = {
    pending: { color: 'default', label: 'Pending' },
    running: { color: 'processing', label: 'Running' },
    completed: { color: 'success', label: 'Completed' },
    failed: { color: 'error', label: 'Failed' },
};

function formatDuration(startedAt: string, completedAt: string | null): string {
    if (!completedAt) return '-';
    const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function titleCase(s: string): string {
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Maps consistency field keys to the canonical pipeline agent + quality-metrics label,
// so the drilldown mirrors the ordering/labels used in the per-question (quality) view.
const CONSISTENCY_FIELD_TO_AGENT: Record<string, string> = {
    intent: 'intent',
    refine_output: 'refine',
    planner_plan: 'planner',
    narrative_structured: 'narrative',
    sql_output: 'sql',
    viz_output: 'visualization',
    task_narrative: 'task_narrative',
};

const CONSISTENCY_FIELD_LABEL: Record<string, string> = {
    intent: 'Intent',
    refine_output: 'Refine',
    planner_plan: 'Plan',
    narrative_structured: 'Overall Summary',
    sql_output: 'SQL',
    viz_output: 'Visualization',
    task_narrative: 'Task Narrative',
};

function consistencyFieldLabel(field: string): string {
    return CONSISTENCY_FIELD_LABEL[field] ?? titleCase(field);
}

function compareConsistencyFields(a: string, b: string): number {
    return compareAgents(CONSISTENCY_FIELD_TO_AGENT[a] ?? a, CONSISTENCY_FIELD_TO_AGENT[b] ?? b);
}

function humanizeMetricKey(key: string): string {
    const segment = key.includes('.') ? key.split('.').pop()! : key;
    return titleCase(segment);
}

type AgentEvaluationSummary = {
    evaluators: number;
    passed: number;
    failed: number;
    unknown: number;
    scoreSum: number;
    scoreCount: number;
};

function gateEntriesForPrefixes(row: EvalPerQuestionResult, prefixes: string[]): Array<[string, boolean]> {
    return Object.entries(row.gate_metrics ?? {}).filter(([key]) =>
        prefixes.some((prefix) => gateKeyMatchesPrefix(key, prefix)),
    );
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

/** Normalized score (0-1) to color */
function scoreColor(pct: number): string {
    if (pct >= 70) return '#52c41a';
    if (pct >= 40) return '#faad14';
    return '#ff4d4f';
}

/** Detect metric scale and return percent for Progress bar, or null to skip */
function metricPercent(value: number): number | null {
    if (value >= 0 && value <= 1) return Math.round(value * 100);
    if (value >= 0 && value <= 5) return Math.round((value / 5) * 100);
    return null;
}

// --- Shared output formatters (used by both the per-question expanded row and the consistency drilldown) ---

function formatOutput(value: unknown): React.ReactNode {
    if (value == null) return <Text type="secondary">-</Text>;
    if (typeof value === 'string') {
        if (value.length > 300) return <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto' }}>{value}</div>;
        return <span style={{ wordBreak: 'break-word' }}>{value}</span>;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return String(value);
    return (
        <pre style={{ fontSize: 11, margin: 0, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fafafa', padding: 8, borderRadius: 4 }}>
            {JSON.stringify(value, null, 2)}
        </pre>
    );
}

function tryParseJson(v: unknown): unknown {
    if (typeof v === 'string') {
        try { return JSON.parse(v); } catch { /* noop */ }
        // Handle NDJSON (newline-delimited JSON) and "[label]: json" prefixes per line
        if (v.includes('\n')) {
            const items = v.split('\n').filter(Boolean).map((line) => {
                try { return JSON.parse(line); } catch { /* noop */ }
                const stripped = line.replace(/^\[[^\]]*\]:\s*/, '');
                if (stripped !== line) { try { return JSON.parse(stripped); } catch { /* noop */ } }
                return null;
            }).filter(Boolean);
            if (items.length > 0) return items;
        }
        const stripped = v.replace(/^\[[^\]]*\]:\s*/, '');
        if (stripped !== v) { try { return JSON.parse(stripped); } catch { /* noop */ } }
        return v;
    }
    return v;
}

function toTableArray(raw: unknown): unknown[] {
    if (raw == null) return [];
    const parsed = tryParseJson(raw);
    if (Array.isArray(parsed)) {
        return parsed.flatMap(item => Array.isArray(item) ? item : [item]);
    }
    if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>;
        if ('columns' in obj && 'rows' in obj) return [obj];
        return Object.values(obj).filter(v => v && typeof v === 'object');
    }
    return [];
}

function parseArray(raw: unknown): unknown[] {
    const parsed = tryParseJson(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
    return [];
}

function renderSqlTable(table: unknown, idx: number): React.ReactNode {
    const t = (typeof table === 'object' && table) ? table as Record<string, unknown> : null;
    if (!t) return formatOutput(table);
    const cols = t.columns as string[] | undefined;
    const rawRows = t.rows as unknown[] | undefined;
    if (!cols?.length || !rawRows?.length) return formatOutput(table);
    const rows = rawRows.map((row) => {
        if (Array.isArray(row)) {
            const obj: Record<string, unknown> = {};
            cols.forEach((col, ci) => { obj[col] = ci < row.length ? row[ci] : null; });
            return obj;
        }
        return row as Record<string, unknown>;
    });
    return (
        <Table
            key={idx}
            size="small"
            pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
            scroll={{ x: true }}
            dataSource={rows.map((r, i) => ({ ...r, _key: i }))}
            rowKey="_key"
            columns={cols.map((c) => ({
                title: c, dataIndex: c, key: c, ellipsis: true,
                render: (v: unknown) => v == null ? '-' : String(v),
            }))}
        />
    );
}

function renderVizOutput(viz: unknown, idx: number, fallbackSql?: TaskResultSqlOutput): React.ReactNode {
    const v = (typeof viz === 'object' && viz) ? viz as Record<string, unknown> : null;
    if (!v) return formatOutput(viz);
    const data = (v.viz_data ?? v) as Record<string, unknown>;
    const chartType = (data.chart_type ?? v.chart_type) as string | undefined;
    const chartTitle = (data.chartTitle ?? data.chart_title ?? v.chartTitle ?? v.chart_title) as string | undefined;
    return (
        <div key={idx}>
            <SkillStudioVizPreview
                chartType={chartType}
                chartTitle={chartTitle}
                vizData={data}
                sqlOutput={fallbackSql}
            />
        </div>
    );
}

function renderNarrativeContent(value: unknown): React.ReactNode {
    if (typeof value === 'string') {
        return <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto' }}>{value}</div>;
    }
    if (value && typeof value === 'object') {
        const narr = value as Record<string, unknown>;
        const overview = narr.overview as string | undefined;
        const takeaways = narr.key_takeaways as string[] | undefined;
        const assumptions = narr.assumptions as string[] | undefined;
        const formulae = narr.formulae as string[] | undefined;
        const renderList = (title: string, items?: string[]) => (
            items?.length ? (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{title}</Text>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                        {items.map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                    </ul>
                </div>
            ) : null
        );
        if (overview || takeaways?.length || assumptions?.length || formulae?.length) {
            return (
                <div>
                    {overview && <div style={{ fontSize: 12, marginBottom: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{overview}</div>}
                    {renderList('Key Takeaways', takeaways)}
                    {renderList('Assumptions', assumptions)}
                    {renderList('Formulae', formulae)}
                </div>
            );
        }
    }
    return formatOutput(value);
}

/** Render a single consistency per-run output using the same formatters as the quality-metrics row. */
function renderConsistencyOutput(fieldName: string, output: unknown): React.ReactNode {
    if (output == null) return <Text type="secondary">-</Text>;
    if (fieldName === 'viz_output') {
        const vizList = parseArray(output);
        if (vizList.length > 0) {
            return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{vizList.map((viz, i) => renderVizOutput(viz, i))}</div>;
        }
        return formatOutput(output);
    }
    if (fieldName === 'narrative_structured') {
        return renderNarrativeContent(tryParseJson(output));
    }
    if (fieldName === 'sql_output' || fieldName === 'context') {
        const tables = toTableArray(output);
        if (tables.length > 0) {
            return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{tables.map((t, i) => renderSqlTable(t, i))}</div>;
        }
        return formatOutput(output);
    }
    return formatOutput(tryParseJson(output));
}

/** Filter out noisy metric keys: gpt_* duplicates, token counts, binary_aggregate */
function isDisplayMetric(key: string): boolean {
    const last = key.includes('.') ? key.split('.').pop()! : key;
    if (last.startsWith('gpt_')) return false;
    if (last.endsWith('_tokens') || last.endsWith('_prompt_tokens') || last.endsWith('_completion_tokens') || last.endsWith('_total_tokens')) return false;
    if (last === 'binary_aggregate') return false;
    return true;
}

function groupMetricsByAgent(
    metrics: Record<string, unknown>,
): Record<string, Record<string, number>> {
    const grouped: Record<string, Record<string, number>> = {};
    for (const [key, value] of Object.entries(metrics)) {
        if (typeof value !== 'number') continue;
        const prefix = key.includes('.')
            ? key.substring(0, key.indexOf('.'))
            : key.split('_')[0];
        const agentName = prefix.split('_')[0];
        if (!grouped[agentName]) grouped[agentName] = {};
        grouped[agentName][key] = value;
    }
    return grouped;
}

function failedGateEntries(row: EvalPerQuestionResult): Array<[string, boolean]> {
    return Object.entries(row.gate_metrics ?? {}).filter(([, passed]) => !passed);
}

function failedGateAgents(row: EvalPerQuestionResult, groups = gateDisplayGroups): string[] {
    const grouped = groups
        .filter((group) => gateEntriesForPrefixes(row, group.prefixes).some(([, passed]) => !passed))
        .map((group) => group.label);
    const groupedKeys = new Set(groups.flatMap((group) => group.prefixes));
    const ungrouped = failedGateEntries(row)
        .filter(([key]) => !Array.from(groupedKeys).some((prefix) => gateKeyMatchesPrefix(key, prefix)))
        .map(([key]) => titleCase(key.split('.')[0]));
    return Array.from(new Set([...grouped, ...ungrouped]));
}

type PerQuestionRow = EvalPerQuestionResult;

type QuestionFilter = 'all' | 'passed' | 'failed' | 'errors';

export interface SkillStudioEvalRunDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    loading: boolean;
    results: EvalRunResultsResponse | null;
    downloading: boolean;
    onDownload: (format: 'csv' | 'json') => void;
}

/** Aggregate table for a semantic-consistency section (plan_structure / whole_response). */
const ConsistencyScoreTable: React.FC<{ title: string; stats: Record<string, ConsistencyScoreStat> }> = ({ title, stats }) => {
    const rows = Object.entries(stats)
        .sort(([a], [b]) => compareAgents(a, b))
        .map(([metric, s]) => ({ metric, mean: s.mean, min: s.min }));
    if (rows.length === 0) return null;
    return (
        <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>{title}</Text>
            <Table
                size="small"
                pagination={false}
                rowKey="metric"
                dataSource={rows}
                columns={[
                    { title: 'Metric', dataIndex: 'metric', key: 'metric', render: (v: string) => titleCase(v) },
                    {
                        title: 'Mean',
                        dataIndex: 'mean',
                        key: 'mean',
                        width: 280,
                        render: (v: number) => {
                            const pct = metricPercent(v);
                            if (pct == null) return <Text strong style={{ fontSize: 12 }}>{v.toFixed(2)}</Text>;
                            return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Progress percent={pct} size="small" showInfo={false} strokeColor={scoreColor(pct)} style={{ flex: 1, margin: 0 }} />
                                    <Text strong style={{ fontSize: 12, minWidth: 56 }}>{pct}% ({v.toFixed(1)})</Text>
                                </div>
                            );
                        },
                    },
                    {
                        title: 'Min',
                        dataIndex: 'min',
                        key: 'min',
                        width: 120,
                        align: 'center',
                        render: (v: number) => {
                            const pct = metricPercent(v);
                            if (pct == null) return <Tag>{v.toFixed(2)}</Tag>;
                            return <Tag color={pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error'}>{pct}%</Tag>;
                        },
                    },
                ]}
            />
        </div>
    );
};

/** Per-task consistency drilldown grouped by question. */
const ConsistencyPerTaskPanel: React.FC<{ section: ConsistencyPerTaskSection }> = ({ section }) => {
    const questions = section.per_question?.filter((pq) => pq.tasks?.length) ?? [];
    if (questions.length === 0) return null;
    return (
        <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Per-Task Consistency</Text>
            <Collapse
                size="small"
                items={questions.map((pq, idx) => ({
                    key: String(idx),
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text ellipsis style={{ flex: 1 }}>{pq.group}</Text>
                            <Tag>{pq.tasks.length} task{pq.tasks.length !== 1 ? 's' : ''}</Tag>
                        </div>
                    ),
                    children: (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {pq.tasks.map((task, tIdx) => (
                                <div key={tIdx}>
                                    <Space size={8} style={{ marginBottom: 4 }} wrap>
                                        <Text strong style={{ fontSize: 12 }}>{titleCase(task.task)}</Text>
                                        <Tag color={task.task_presence_consistent ? 'success' : 'error'}>
                                            {task.task_presence_consistent ? 'presence consistent' : 'presence varies'}
                                        </Tag>
                                        <Text type="secondary" style={{ fontSize: 11 }}>present in {task.runs_present} run{task.runs_present !== 1 ? 's' : ''}</Text>
                                    </Space>
                                    <Space size={4} wrap>
                                        {Object.entries(task.metrics)
                                            .sort(([a], [b]) => compareAgents(a, b))
                                            .map(([metric, s]) => {
                                                const pct = metricPercent(s.mean);
                                                const label = pct == null ? s.mean.toFixed(2) : `${pct}%`;
                                                return (
                                                    <Tooltip key={metric} title={`${titleCase(metric)}: mean ${s.mean.toFixed(2)}, min ${s.min.toFixed(2)}`}>
                                                        <Tag color={pct == null ? undefined : pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error'}>
                                                            {titleCase(metric)}: {label}
                                                        </Tag>
                                                    </Tooltip>
                                                );
                                            })}
                                    </Space>
                                </div>
                            ))}
                        </div>
                    ),
                }))}
            />
        </div>
    );
};

const SkillStudioEvalRunDetailDrawer: React.FC<SkillStudioEvalRunDetailDrawerProps> = ({
    open,
    onClose,
    loading,
    results,
    downloading,
    onDownload,
}) => {
    const [questionFilter, setQuestionFilter] = useState<QuestionFilter>('all');

    useEffect(() => {
        if (!open) setQuestionFilter('all');
    }, [open]);

    const run = results?.run;
    const targetAgent = run?.target_agent || null;
    const activeGateDisplayGroups = useMemo(
        () => gateDisplayGroups.filter((group) => targetAgentMatchesGroup(targetAgent, group)),
        [targetAgent],
    );

    const questionRows: PerQuestionRow[] = useMemo(
        () => results?.per_question_results ?? [],
        [results],
    );

    // --- Pass rate stats ---
    const passCount = questionRows.filter((r) => r.row_pass === true).length;
    const failCount = questionRows.filter((r) => r.row_pass === false).length;
    const errorCount = questionRows.filter((r) => r.error != null).length;
    const totalCount = questionRows.length;
    const passPercent = totalCount > 0 ? Math.round((passCount / totalCount) * 100) : 0;
    const failedGateCount = questionRows.reduce(
        (sum, row) => sum + activeGateDisplayGroups.reduce(
            (groupSum, group) => groupSum + gateEntriesForPrefixes(row, group.prefixes).filter(([, passed]) => !passed).length,
            0,
        ),
        0,
    );
    const failingAgents = useMemo(
        () => Array.from(new Set(questionRows.flatMap((row) => failedGateAgents(row, activeGateDisplayGroups)))).sort(compareAgents),
        [questionRows, activeGateDisplayGroups],
    );
    const runWarning = useMemo(() => {
        const foundryError = results?.overview_metrics?.['foundry.combined_error'];
        if (typeof foundryError === 'string' && foundryError.length > 0) return foundryError;
        if (run?.status !== 'failed' && run?.error_message) return run.error_message;
        return null;
    }, [results?.overview_metrics, run?.error_message, run?.status]);

    // --- Filter ---
    const filteredRows = useMemo(() => {
        switch (questionFilter) {
            case 'passed':
                return questionRows.filter((r) => r.row_pass === true);
            case 'failed':
                return questionRows.filter((r) => r.row_pass === false);
            case 'errors':
                return questionRows.filter((r) => r.error != null);
            default:
                return questionRows;
        }
    }, [questionRows, questionFilter]);

    const renderGateStripStatus = (record: PerQuestionRow, group: GateDisplayGroup) => {
        const entries = gateEntriesForPrefixes(record, group.prefixes);
        const failed = entries.filter(([, passed]) => !passed);
        const tooltip = entries.length > 0
            ? entries.map(([key, passed]) => `${key}: ${passed ? 'passed' : 'failed'}`).join('\n')
            : `No ${group.label} gate captured`;

        return (
            <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{tooltip}</div>}>
                {entries.length === 0 ? (
                    <WarningOutlined style={{ color: '#d9d9d9' }} />
                ) : failed.length > 0 ? (
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                ) : (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                )}
            </Tooltip>
        );
    };

    const questionColumns: ColumnsType<PerQuestionRow> = useMemo(() => {
        return [
            { title: '#', dataIndex: 'question_index', key: 'idx', width: 56, align: 'center' },
            {
                title: 'Question',
                dataIndex: 'query',
                key: 'query',
                ellipsis: true,
            },
            {
                title: 'Result',
                dataIndex: 'row_pass',
                key: 'row_pass',
                width: 80,
                align: 'center',
                render: (v: boolean | null, record: PerQuestionRow) =>
                    record.error ? (
                        <Tooltip title={record.error}>
                            <WarningOutlined style={{ color: '#faad14' }} />
                        </Tooltip>
                    ) : v == null ? (
                        <WarningOutlined style={{ color: '#d9d9d9' }} />
                    ) : v ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    ),
            },
            ...activeGateDisplayGroups.map((group): ColumnsType<PerQuestionRow>[number] => ({
                title: group.label,
                key: `gate-${group.key}`,
                width: group.key === 'overall_narrative' || group.key === 'task_narrative' ? 126 : 96,
                align: 'center',
                render: (_: unknown, record: PerQuestionRow) => renderGateStripStatus(record, group),
            })),
        ];
    }, [activeGateDisplayGroups]);

    // --- Metrics ---
    const metricsGrouped = results?.overview_metrics
        ? groupMetricsByAgent(results.overview_metrics)
        : null;

    const agentEvaluationMetrics = useMemo(() => {
        const grouped: Record<string, AgentEvaluationSummary> = {};

        questionRows.forEach((row) => {
            Object.entries(row.agent_evaluations ?? {}).forEach(([agent, evaluatorMap]) => {
                if (!grouped[agent]) {
                    grouped[agent] = {
                        evaluators: 0,
                        passed: 0,
                        failed: 0,
                        unknown: 0,
                        scoreSum: 0,
                        scoreCount: 0,
                    };
                }

                Object.values(evaluatorMap ?? {}).forEach((details) => {
                    const detail = asRecord(details);
                    if (!detail) return;
                    grouped[agent].evaluators += 1;
                    if (detail.passed === true) grouped[agent].passed += 1;
                    else if (detail.passed === false) grouped[agent].failed += 1;
                    else grouped[agent].unknown += 1;

                    if (typeof detail.score === 'number') {
                        grouped[agent].scoreSum += detail.score;
                        grouped[agent].scoreCount += 1;
                    }
                });
            });
        });

        return grouped;
    }, [questionRows]);

    const consistency = useMemo<ConsistencyMetrics | null>(() => {
        const raw = results?.overview_metrics?.consistency;
        if (!raw || typeof raw !== 'object') return null;
        return raw as ConsistencyMetrics;
    }, [results]);

    const evaluatorMetadata = useMemo<Record<string, unknown>>(() => {
        const raw = results?.overview_metrics?.evaluator_metadata;
        return raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
    }, [results?.overview_metrics]);

    // --- Expanded row ---
    const renderExpandedRow = (record: PerQuestionRow) => {
        const gt = record.ground_truth as Record<string, unknown> | null;
        const ae = record.agent_evaluations;
        const ao = record.agent_outputs;
        const gm = record.gate_metrics;
        const combinedEvaluations = record.combined_evaluations;

        const firstPresent = (...values: unknown[]) =>
            values.find((value) => value != null && !(typeof value === 'string' && value.trim() === ''));

        const hasMeaningfulValue = (value: unknown): boolean => {
            if (value == null) return false;
            if (typeof value === 'string') return value.trim().length > 0;
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'object') {
                return Object.entries(value as Record<string, unknown>)
                    .filter(([key]) => !['task', 'task_name', 'name', 'matched'].includes(key))
                    .some(([, nested]) => hasMeaningfulValue(nested));
            }
            return true;
        };

        const renderSqlQueries = (queries: unknown): React.ReactNode => {
            const parsed = tryParseJson(queries);
            const list = Array.isArray(parsed) ? parsed : (typeof parsed === 'string' ? [parsed] : []);
            if (list.length === 0) return formatOutput(parsed);
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {list.map((query, idx) => (
                        <pre key={idx} style={{ fontSize: 11, margin: 0, padding: 8, background: '#f6f8fa', borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 280, overflow: 'auto' }}>
                            {String(query)}
                        </pre>
                    ))}
                </div>
            );
        };

        const renderHitlValue = (value: unknown) => {
            const obj = asRecord(value);
            if (!obj) return formatOutput(value);
            const rows = ([
                ['Expected', obj.expected],
                ['Scripted response', obj.scripted_response],
                ['Scripted responses', obj.scripted_responses],
                ['Validation', obj.validation],
                ['Interrupt occurred', obj.interrupt_occurred_as_expected],
                ['Used clarified value', obj.used_clarified_value],
                ['Avoided wrong value', obj.avoided_wrong_value],
                ['Completed successfully', obj.completed_successfully],
                ['Validation passed', obj.validation_passed],
                ['Final response preview', obj.final_response_preview],
                ['Refine output preview', obj.refine_output_preview],
                ['Had interrupt', obj.had_interrupt],
                ['Agents that interrupted', obj.agents_that_interrupted],
                ['HITL turns', obj.hitl_turns],
            ] as Array<[string, unknown]>).filter(([, rowValue]) => rowValue != null);

            if (rows.length === 0) return formatOutput(value);

            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {rows.map(([label, rowValue]) => (
                        <div key={label}>
                            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 2 }}>{label}</Text>
                            {formatOutput(rowValue)}
                        </div>
                    ))}
                </div>
            );
        };

        const renderOverallNarrativeResponse = () => {
            const narrative = asRecord(ao?.narrative);
            const structuredNarrative = asRecord(ao?.narrative_structured);
            const primaryNarrative = structuredNarrative ?? narrative ?? firstPresent(ao?.narrative_structured, ao?.narrative);

            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {renderNarrativeContent(primaryNarrative)}
                    {ao?.narrative_full != null && (
                        <Collapse
                            size="small"
                            items={[{
                                key: 'narrative-full',
                                label: <Text strong>Full final answer</Text>,
                                children: formatOutput(ao.narrative_full),
                            }]}
                        />
                    )}
                </div>
            );
        };

        const sqlArr = ao ? toTableArray(ao.sql_output) : [];
        const vizArr = ao ? parseArray(ao.viz_output) : [];
        const ctxArr = ao ? toTableArray(ao.context) : [];
        const narrativeArr: unknown[] = ao?.narrative != null
            ? (Array.isArray(ao.narrative) ? ao.narrative : [ao.narrative])
            : [];
        const sqlQueryRaw = ao ? tryParseJson(ao.sql_query ?? ao.sql_queries) : undefined;
        const sqlQueries: string[] = (() => {
            if (!sqlQueryRaw) return [];
            if (typeof sqlQueryRaw === 'string') return [sqlQueryRaw];
            if (Array.isArray(sqlQueryRaw)) return sqlQueryRaw.filter((q): q is string => typeof q === 'string');
            return [];
        })();
        const perTaskRaw = ao?.per_task as Record<string, unknown> | undefined;
        const perTaskDetails: Array<Record<string, unknown>> | undefined = perTaskRaw ? (() => {
            let ptd = perTaskRaw.per_task_details;
            if (typeof ptd === 'string') { try { ptd = JSON.parse(ptd); } catch { return undefined; } }
            return Array.isArray(ptd) ? ptd as Array<Record<string, unknown>> : undefined;
        })() : undefined;
        const taskSqlDetails = Array.isArray(asRecord(ao?.task_sql)?.task_sql_details)
            ? asRecord(ao?.task_sql)?.task_sql_details as Array<Record<string, unknown>>
            : [];
        const taskVisualizationDetails = Array.isArray(asRecord(ao?.task_visualization)?.task_visualization_details)
            ? asRecord(ao?.task_visualization)?.task_visualization_details as Array<Record<string, unknown>>
            : [];
        const taskNarrativeDetails = Array.isArray(asRecord(ao?.task_narrative)?.task_narrative_details)
            ? asRecord(ao?.task_narrative)?.task_narrative_details as Array<Record<string, unknown>>
            : [];
        const hasPerTaskDetails = (perTaskDetails?.length ?? 0) > 0;
        const questionResponse = firstPresent(
            ao?.question,
            ao?.query,
            ao?.user_query,
            ao?.input_query,
            ao?.refined_query,
        );

        type ReviewRow = {
            key: string;
            label: string;
            groundTruth: unknown;
            response: unknown;
            evalPrefixes: string[];
            renderGroundTruth?: (value: unknown) => React.ReactNode;
            renderResponse?: (value: unknown) => React.ReactNode;
        };

        const gateEntries = gm ? Object.entries(gm) : [];
        const failedGateEntries = gateEntries.filter(([, passed]) => !passed);
        const getGateEntriesForPrefixes = (prefixes: string[]) =>
            gateEntries.filter(([key]) => prefixes.some((prefix) => gateKeyMatchesPrefix(key, prefix)));
        const scopedFailedGateEntries = targetAgent
            ? activeGateDisplayGroups.flatMap((group) => getGateEntriesForPrefixes(group.prefixes).filter(([, passed]) => !passed))
            : failedGateEntries;

        const getEvaluatorMetadata = (agentKey: string, evalName: string): Record<string, unknown> => {
            const agentMetadata = asRecord(evaluatorMetadata[agentKey]);
            const directMetadata = asRecord(evaluatorMetadata[evalName]);
            return asRecord(agentMetadata?.[evalName]) ?? directMetadata ?? {};
        };

        const renderGateSummary = (prefixes: string[]) => {
            const entries = getGateEntriesForPrefixes(prefixes);
            const evalBlocks = ae
                ? prefixes.flatMap((prefix) => {
                    return Object.entries(ae)
                        .filter(([agentKey]) => gateKeyMatchesPrefix(agentKey, prefix))
                        .flatMap(([agentKey, evaluatorMap]) =>
                            Object.entries(evaluatorMap ?? {}).map(([evalName, details]) => ({
                                agentKey,
                                evalName,
                                details: details as Record<string, unknown>,
                            })),
                        );
                })
                : [];
            const uniqueEvalBlocks = Array.from(new Map(
                evalBlocks.map((block) => [`${block.agentKey}.${block.evalName}`, block]),
            ).values());

            if (entries.length === 0 && uniqueEvalBlocks.length === 0) return <Text type="secondary">No eval captured</Text>;

            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {entries.length > 0 && (
                        <Space size={4} wrap>
                            {entries.map(([key, passed]) => (
                                <Tooltip key={key} title={key}>
                                    <Tag color={passed ? 'success' : 'error'} style={{ margin: 0 }}>
                                        {humanizeMetricKey(key)}
                                    </Tag>
                                </Tooltip>
                            ))}
                        </Space>
                    )}
                    {uniqueEvalBlocks.map(({ agentKey, evalName, details }) => {
                        const metadata = getEvaluatorMetadata(agentKey, evalName);
                        const legacyResult = details[`${evalName}_result`] as string | undefined;
                        const label = String(details.label ?? metadata.label ?? titleCase(evalName));
                        const indicates = details.indicates ?? metadata.indicates ?? details.default_threshold_note ?? metadata.default_threshold_note;
                        const score = typeof details.score === 'number'
                            ? details.score
                            : typeof details[evalName] === 'number'
                                ? details[evalName] as number
                                : undefined;
                        const threshold = typeof details.threshold === 'number'
                            ? details.threshold
                            : typeof details[`${evalName}_threshold`] === 'number'
                                ? details[`${evalName}_threshold`] as number
                                : undefined;
                        const passed = typeof details.passed === 'boolean' ? details.passed : legacyResult === 'pass';
                        const failed = typeof details.passed === 'boolean' ? !details.passed : legacyResult === 'fail';
                        const explanation = (details.explanation ?? metadata.explanation) as string | null | undefined;
                        const reason = (details.reason ?? explanation ?? details[`${evalName}_reason`] ?? details[`${evalName}_details`]) as string | null | undefined;
                        return (
                            <div key={`${agentKey}.${evalName}`} style={{ fontSize: 11, padding: '4px 6px', background: failed ? '#fff2f0' : passed ? '#f6ffed' : '#fafafa', borderRadius: 4 }}>
                                <Space size={4} wrap>
                                    <Tooltip title={indicates ? String(indicates) : undefined}>
                                        <Text strong style={{ fontSize: 11 }}>{titleCase(agentKey)} / {label}</Text>
                                    </Tooltip>
                                    <Tag color={passed ? 'success' : failed ? 'error' : 'default'} style={{ margin: 0 }}>
                                        {passed ? 'pass' : failed ? 'fail' : 'unknown'}
                                    </Tag>
                                    {score != null && <Text type="secondary" style={{ fontSize: 11 }}>score {score.toFixed(2)}</Text>}
                                    {threshold != null && <Text type="secondary" style={{ fontSize: 11 }}>threshold {threshold.toFixed(2)}</Text>}
                                </Space>
                                {indicates != null && <div style={{ marginTop: 3, color: '#666', lineHeight: '16px' }}>{String(indicates)}</div>}
                                {reason ? (
                                    <div style={{ marginTop: 3, color: '#666', lineHeight: '16px' }}>{reason}</div>
                                ) : (
                                    <Text type="secondary" style={{ display: 'block', marginTop: 3, fontSize: 11 }}>No evaluator explanation returned.</Text>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        };

        const renderTables = (tables: unknown[]) => (
            tables.length > 0
                ? <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{tables.map((table, idx) => renderSqlTable(table, idx))}</div>
                : <Text type="secondary">-</Text>
        );

        const renderVisualizations = (items: unknown[], fallbackSql?: TaskResultSqlOutput) => (
            items.length > 0
                ? <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{items.map((viz, idx) => renderVizOutput(viz, idx, fallbackSql))}</div>
                : <Text type="secondary">-</Text>
        );

        const renderNarrativePart = (value: unknown, part: 'key_takeaways' | 'assumptions' | 'formulae') => {
            const obj = asRecord(value);
            const items = obj?.[part];
            if (!Array.isArray(items) || items.length === 0) return <Text type="secondary">-</Text>;
            return (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                    {items.map((item, idx) => <li key={idx} style={{ marginBottom: 4 }}>{String(item)}</li>)}
                </ul>
            );
        };

        const renderNarrativePartPanel = (value: unknown, part: 'assumptions' | 'formulae', title: string) => (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8, background: '#fafafa' }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 6 }}>{title}</Text>
                {renderNarrativePart(value, part)}
            </div>
        );

        const renderPlan = (value: unknown) => {
            const obj = asRecord(value);
            const tasks = Array.isArray(value)
                ? value
                : Array.isArray(obj?.tasks)
                    ? obj?.tasks
                    : [];
            if (tasks.length === 0) return formatOutput(value);
            return (
                <Table
                    size="small"
                    pagination={false}
                    rowKey="_idx"
                    dataSource={tasks.map((task, idx) => ({ ...(asRecord(task) ?? { value: task }), _idx: idx }))}
                    columns={[
                        { title: 'Task', dataIndex: 'task', key: 'task', width: 160, render: (_: unknown, row: Record<string, unknown>) => String(row.task ?? row.name ?? '-') },
                        { title: 'Sub-query', dataIndex: 'sub_query', key: 'sub_query', render: (value: unknown) => value == null ? '-' : String(value) },
                    ]}
                />
            );
        };

        const renderReviewValue = (value: unknown, customRenderer?: (value: unknown) => React.ReactNode) => {
            if (customRenderer) return customRenderer(value);
            if (Array.isArray(value)) return formatOutput(value);
            if (value && typeof value === 'object') return renderNarrativeContent(value);
            return formatOutput(value);
        };

        const renderValuePanel = (title: string, children: React.ReactNode) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>{title}</Text>
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, background: '#fff' }}>
                    {children}
                </div>
            </div>
        );

        const renderReviewCards = (rows: ReviewRow[]) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {rows.map((row) => (
                    <div key={row.key} style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '8px 10px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                            <Text strong style={{ fontSize: 12, minWidth: 120 }}>{row.label}</Text>
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                {renderGateSummary(row.evalPrefixes)}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10 }}>
                            {renderValuePanel('Ground Truth', renderReviewValue(row.groundTruth, row.renderGroundTruth))}
                            {renderValuePanel('Agent Response', renderReviewValue(row.response, row.renderResponse))}
                        </div>
                    </div>
                ))}
            </div>
        );

        const renderTaskReviewTabs = (rows: ReviewRow[]) => (
            <Tabs
                size="small"
                items={rows.map((row) => ({
                    key: row.key,
                    label: row.label,
                    children: renderReviewCards([row]),
                }))}
            />
        );

        const gtPerTasks = Array.isArray(gt?.GroundTruthPerTask) ? gt?.GroundTruthPerTask as Array<Record<string, unknown>> : [];
        const actualTaskDetails = perTaskDetails ?? [];
        const hasStructuredTaskDetails = taskSqlDetails.length > 0 || taskVisualizationDetails.length > 0 || taskNarrativeDetails.length > 0;
        const legacyTaskCount = hasPerTaskDetails || hasStructuredTaskDetails ? 0 : Math.max(sqlQueries.length, sqlArr.length, vizArr.length, narrativeArr.length);
        const taskCount = Math.max(gtPerTasks.length, actualTaskDetails.length, taskSqlDetails.length, taskVisualizationDetails.length, taskNarrativeDetails.length, legacyTaskCount);
        const refinePrefixes = gateDisplayGroups.find((group) => group.key === 'refine')?.prefixes ?? ['refine'];
        const plannerPrefixes = gateDisplayGroups.find((group) => group.key === 'planner')?.prefixes ?? ['planner'];
        const overallNarrativePrefixes = gateDisplayGroups.find((group) => group.key === 'overall_narrative')?.prefixes ?? ['narrative_structured'];
        const hitlActual = asRecord(ao?.hitl_validation)?.actual;
        const hitlAgentSpecific = asRecord(asRecord(ao?.hitl_validation)?.agent_specific);
        const plannerHitlSpecific = asRecord(hitlAgentSpecific?.planner);
        const refineHitlSpecific = asRecord(hitlAgentSpecific?.refine_agent) ?? asRecord(hitlAgentSpecific?.refine);
        const plannerHitlResponse = {
            ...(asRecord(hitlActual) ?? {}),
            ...(plannerHitlSpecific ?? {}),
        };
        const refineHitlResponse = {
            ...(asRecord(hitlActual) ?? {}),
            ...(refineHitlSpecific ?? {}),
        };
        const plannerHitlRow: ReviewRow | null = hasMeaningfulValue(asRecord(gt?.GroundTruthHITL)?.planner) || hasMeaningfulValue(plannerHitlSpecific)
            ? {
                key: 'planner-hitl',
                label: 'HITL',
                groundTruth: asRecord(gt?.GroundTruthHITL)?.planner,
                response: plannerHitlResponse,
                evalPrefixes: plannerPrefixes,
                renderGroundTruth: renderHitlValue,
                renderResponse: renderHitlValue,
            }
            : null;
        const refineHitlRow: ReviewRow | null = hasMeaningfulValue(asRecord(gt?.GroundTruthHITL)?.refine_agent ?? asRecord(gt?.GroundTruthHITL)?.refine) || hasMeaningfulValue(refineHitlSpecific)
            ? {
                key: 'refine-hitl',
                label: 'HITL',
                groundTruth: asRecord(gt?.GroundTruthHITL)?.refine_agent ?? asRecord(gt?.GroundTruthHITL)?.refine,
                response: refineHitlResponse,
                evalPrefixes: refinePrefixes,
                renderGroundTruth: renderHitlValue,
                renderResponse: renderHitlValue,
            }
            : null;

        const topLevelRows: Array<{ key: string; title: string; rows: ReviewRow[]; statusPrefixes: string[] }> = [
            {
                key: 'question',
                title: 'Question',
                statusPrefixes: [],
                rows: [{ key: 'question', label: 'Question', groundTruth: record.query, response: questionResponse, evalPrefixes: [] }],
            },
            {
                key: 'intent',
                title: 'Intent',
                statusPrefixes: ['intent'],
                rows: [{ key: 'intent', label: 'Intent', groundTruth: gt?.GroundTruthIntent, response: ao?.intent ?? record.intent, evalPrefixes: ['intent'] }],
            },
            {
                key: 'refine',
                title: 'Refine',
                statusPrefixes: refinePrefixes,
                rows: [
                    { key: 'refine', label: 'Refine', groundTruth: gt?.GroundTruthRefine, response: ao?.refine_output, evalPrefixes: refinePrefixes },
                    ...(refineHitlRow ? [refineHitlRow] : []),
                ],
            },
            {
                key: 'plan',
                title: 'Plan',
                statusPrefixes: plannerPrefixes,
                rows: [
                    { key: 'plan', label: 'Plan', groundTruth: gt?.GroundTruthPlan, response: ao?.planner_plan, evalPrefixes: plannerPrefixes, renderGroundTruth: renderPlan, renderResponse: renderPlan },
                    ...(plannerHitlRow ? [plannerHitlRow] : []),
                ],
            },
            {
                key: 'overall-summary',
                title: 'Overall Summary',
                statusPrefixes: overallNarrativePrefixes,
                rows: [{ key: 'overall-summary', label: 'Overall Summary', groundTruth: gt?.GroundTruthNarrative ?? gt?.GroundTruthSummary, response: ao?.narrative_structured ?? ao?.narrative ?? ao?.narrative_full, evalPrefixes: overallNarrativePrefixes, renderGroundTruth: renderNarrativeContent, renderResponse: renderOverallNarrativeResponse }],
            },
        ];
        const scopedTopLevelRows = targetAgent
            ? topLevelRows.filter((section) =>
                section.key === 'question' || targetAgentMatchesPrefixes(targetAgent, section.statusPrefixes),
            )
            : topLevelRows;

        const getTaskVisualizations = (task: Record<string, unknown> | undefined, fallback: unknown) => {
            if (Array.isArray(task?.visualizations)) return task?.visualizations as unknown[];
            if (Array.isArray(task?.viz)) return task?.viz as unknown[];
            if (task?.visualizations != null) return [task.visualizations];
            if (task?.viz != null) return [task.viz];
            return fallback == null ? [] : [fallback];
        };

        const taskSections = Array.from({ length: taskCount }, (_, index) => {
            const gtTask = gtPerTasks[index];
            const actualTask = actualTaskDetails[index];
            const sqlTask = taskSqlDetails[index];
            const visualizationTask = taskVisualizationDetails[index];
            const narrativeTask = taskNarrativeDetails[index];
            const hasStructuredTask = hasMeaningfulValue(sqlTask) || hasMeaningfulValue(visualizationTask) || hasMeaningfulValue(narrativeTask);
            if (!gtTask && actualTask && !hasMeaningfulValue(actualTask) && !hasStructuredTask) return null;
            if (!gtTask && !actualTask && (hasPerTaskDetails || hasStructuredTaskDetails) && !hasStructuredTask) return null;
            const gtNarrative = gtTask?.narrative;
            const actualNarrative = asRecord(narrativeTask?.narrative) ?? actualTask?.narrative ?? (!hasPerTaskDetails && !hasStructuredTaskDetails ? narrativeArr[index] : undefined);
            const title = String(gtTask?.task ?? actualTask?.task ?? actualTask?.task_name ?? sqlTask?.task ?? visualizationTask?.task ?? narrativeTask?.task ?? `Task ${index + 1}`);
            const rows: ReviewRow[] = [
                {
                    key: `task-${index}-sql`,
                    label: 'SQL Query',
                    groundTruth: gtTask?.sql_queries,
                    response: sqlTask?.sql_queries ?? actualTask?.sql_queries ?? (!hasPerTaskDetails && !hasStructuredTaskDetails && sqlQueries[index] != null ? [sqlQueries[index]] : undefined),
                    evalPrefixes: ['task_sql', 'sql'],
                    renderGroundTruth: renderSqlQueries,
                    renderResponse: renderSqlQueries,
                },
                {
                    key: `task-${index}-table`,
                    label: 'Table',
                    groundTruth: gtTask?.sql_db_output,
                    response: sqlTask?.sql_db_output ?? actualTask?.sql_db_output ?? (!hasPerTaskDetails && !hasStructuredTaskDetails && sqlArr[index] != null ? [sqlArr[index]] : undefined),
                    evalPrefixes: ['task_sql', 'sql'],
                    renderGroundTruth: (value) => renderTables(Array.isArray(value) ? value : toTableArray(value)),
                    renderResponse: (value) => renderTables(Array.isArray(value) ? value : toTableArray(value)),
                },
                {
                    key: `task-${index}-chart`,
                    label: 'Chart',
                    groundTruth: gtTask?.viz,
                    response: getTaskVisualizations(visualizationTask ?? actualTask, hasPerTaskDetails || hasStructuredTaskDetails ? undefined : vizArr[index]),
                    evalPrefixes: ['task_visualization', 'visualization'],
                    renderGroundTruth: (value) => renderVisualizations(parseArray(value)),
                    renderResponse: (value) => renderVisualizations(Array.isArray(value) ? value : parseArray(value)),
                },
                {
                    key: `task-${index}-takeaways`,
                    label: 'Key takeaways',
                    groundTruth: gtNarrative,
                    response: actualNarrative,
                    evalPrefixes: ['task_narrative'],
                    renderGroundTruth: (value) => renderNarrativePart(value, 'key_takeaways'),
                    renderResponse: (value) => renderNarrativePart(value, 'key_takeaways'),
                },
                {
                    key: `task-${index}-methodology`,
                    label: 'Methodology / Assumptions',
                    groundTruth: gtNarrative,
                    response: actualNarrative,
                    evalPrefixes: ['task_narrative'],
                    renderGroundTruth: (value) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {renderNarrativePartPanel(value, 'assumptions', 'Assumptions')}
                            {renderNarrativePartPanel(value, 'formulae', 'Formulae')}
                        </div>
                    ),
                    renderResponse: (value) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {renderNarrativePartPanel(value, 'assumptions', 'Assumptions')}
                            {renderNarrativePartPanel(value, 'formulae', 'Formulae')}
                        </div>
                    ),
                },
            ];
            const scopedRows = targetAgent
                ? rows.filter((row) => targetAgentMatchesPrefixes(targetAgent, row.evalPrefixes))
                : rows;
            if (targetAgent && scopedRows.length === 0) return null;
            return { key: `task-${index}`, title: `Task ${index + 1}: ${titleCase(title)}`, statusPrefixes: ['execution', 'task_sql', 'task_visualization', 'task_narrative'], rows: scopedRows };
        }).filter((section): section is { key: string; title: string; statusPrefixes: string[]; rows: ReviewRow[] } => section != null);

        const renderSectionLabel = (title: string, prefixes: string[]) => {
            const entries = getGateEntriesForPrefixes(prefixes);
            const failed = entries.filter(([, passed]) => !passed).length;
            return (
                <Space size={8} wrap>
                    <Text strong>{title}</Text>
                    {entries.length > 0 && (
                        <Tag color={failed > 0 ? 'error' : 'success'} style={{ margin: 0 }}>
                            {failed > 0 ? `${failed} failing` : 'passed'}
                        </Tag>
                    )}
                </Space>
            );
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {record.error && (
                    <Alert type="warning" showIcon message={record.error} />
                )}

                {scopedFailedGateEntries.length > 0 && (
                    <Alert
                        type="error"
                        showIcon
                        message="Failed gates"
                        description={(
                            <Space size={4} wrap>
                                {scopedFailedGateEntries.map(([key]) => <Tag key={key} color="error" style={{ margin: 0 }}>{key}</Tag>)}
                            </Space>
                        )}
                    />
                )}

                <Card size="small" title="Accuracy Review">
                    <Collapse
                        size="small"
                        defaultActiveKey={[...scopedTopLevelRows.map((section) => section.key), ...(taskSections.length <= 2 ? taskSections.map((section) => section.key) : [])]}
                        items={[
                            ...scopedTopLevelRows.map((section) => ({
                                key: section.key,
                                label: renderSectionLabel(section.title, section.statusPrefixes),
                                children: renderReviewCards(section.rows),
                            })),
                            ...taskSections.map((section) => ({
                                key: section.key,
                                label: renderSectionLabel(section.title, section.statusPrefixes),
                                children: renderTaskReviewTabs(section.rows),
                            })),
                        ]}
                    />
                </Card>

                <Collapse
                    size="small"
                    items={[{
                        key: 'raw',
                        label: <Text strong>Debug details (raw)</Text>,
                        children: (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                                <Card size="small" title="Ground Truth">{formatOutput(gt)}</Card>
                                <Card size="small" title="Agent Outputs">{formatOutput(ao)}</Card>
                                <Card size="small" title="Gate Metrics">{formatOutput(gm)}</Card>
                                <Card size="small" title="Agent Evaluations">{formatOutput(ae)}</Card>
                                <Card size="small" title="Combined Evaluations">{formatOutput(combinedEvaluations)}</Card>
                                {ctxArr.length > 0 && <Card size="small" title="Context Data">{renderTables(ctxArr)}</Card>}
                            </div>
                        ),
                    }]}
                />
            </div>
        );
    };

    return (
        <Drawer
            title={run ? `Run #${run.id} — ${run.config_name}` : 'Eval Run Details'}
            width="100%"
            open={open}
            onClose={onClose}
            destroyOnClose
            extra={
                run?.status === 'completed' && (
                    <Space>
                        <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            loading={downloading}
                            onClick={() => onDownload('csv')}
                        >
                            CSV
                        </Button>
                        <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            loading={downloading}
                            onClick={() => onDownload('json')}
                        >
                            JSON
                        </Button>
                    </Space>
                )
            }
        >
            {loading && <Spin style={{ display: 'block', margin: '60px auto' }} />}
            {!loading && !run && <Empty description="No results available" />}
            {!loading && run && (
                <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                    {/* ── A. Summary Stats Row ── */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Tag color={statusConfig[run.status].color} style={{ fontSize: 14, padding: '2px 12px' }}>
                                            {statusConfig[run.status].label}
                                        </Tag>
                                    </div>
                                </div>
                                {totalCount > 0 && (
                                    <div style={{ minWidth: 180 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Pass Rate</Text>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                            <Progress
                                                percent={passPercent}
                                                size="small"
                                                showInfo={false}
                                                strokeColor={scoreColor(passPercent)}
                                                style={{ flex: 1, margin: 0 }}
                                            />
                                            <Text strong style={{ color: scoreColor(passPercent) }}>{passCount}/{totalCount}</Text>
                                        </div>
                                    </div>
                                )}
                                <Statistic title="Failed Questions" value={failCount} valueStyle={{ color: failCount > 0 ? '#ff4d4f' : '#52c41a' }} />
                                <Statistic title="Failed Gates" value={failedGateCount} valueStyle={{ color: failedGateCount > 0 ? '#ff4d4f' : '#52c41a' }} />
                                <Statistic title="Errors" value={errorCount} valueStyle={{ color: errorCount > 0 ? '#faad14' : undefined }} />
                                <Statistic title="Questions" value={run.question_count ?? totalCount} />
                                {run.repeats != null && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Repeats</Text>
                                        <div style={{ marginTop: 4 }}>
                                            <Tag color="blue" style={{ fontSize: 14, padding: '2px 12px' }}>{run.repeats}x</Tag>
                                        </div>
                                    </div>
                                )}
                                <Statistic
                                    title="Duration"
                                    value={formatDuration(run.started_at, run.completed_at)}
                                    prefix={<ClockCircleOutlined />}
                                />
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Question Set</Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Text>{run.question_set ?? 'All'}</Text>
                                    </div>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Target Agent</Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Text>{run.target_agent ?? 'Full pipeline'}</Text>
                                    </div>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Triggered By</Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Text><UserOutlined style={{ marginRight: 4 }} />{run.triggered_by ?? '-'}</Text>
                                    </div>
                                </div>
                            </div>
                            {failingAgents.length > 0 && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>Failing agents</Text>
                                    <Space size={4} wrap>
                                        {failingAgents.map((agent) => <Tag key={agent} color="error" style={{ margin: 0 }}>{titleCase(agent)}</Tag>)}
                                    </Space>
                                </div>
                            )}
                        </div>
                    </Card>

                    {runWarning && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Run completed with a warning"
                            description={<div style={{ maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{runWarning}</div>}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {targetAgent && (
                        <Alert
                            type="info"
                            showIcon
                            message={`This run is scoped to ${titleCase(targetAgent)}`}
                            description="Only matching gates, metrics, and review sections are shown. Missing unrelated full-pipeline gates are hidden."
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {run.config_snapshot && Object.keys(run.config_snapshot).length > 0 && (
                        <Collapse
                            size="small"
                            style={{ marginBottom: 16 }}
                            items={[{
                                key: 'config-snapshot',
                                label: (
                                    <Space>
                                        <SettingOutlined />
                                        <Text strong style={{ fontSize: 13 }}>Config Snapshot</Text>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            ({Object.keys(run.config_snapshot).length} keys)
                                        </Text>
                                    </Space>
                                ),
                                children: (
                                    <pre style={{
                                        fontSize: 11,
                                        margin: 0,
                                        maxHeight: 400,
                                        overflow: 'auto',
                                        background: '#fafafa',
                                        padding: 12,
                                        borderRadius: 4,
                                    }}>
                                        {JSON.stringify(run.config_snapshot, null, 2)}
                                    </pre>
                                ),
                            }]}
                        />
                    )}

                    {run.status === 'failed' && run.error_message && (
                        <Alert
                            type="error"
                            showIcon
                            message="Run Failed"
                            description={run.error_message}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* ── B. Metrics by Agent ── */}
                    {Object.keys(agentEvaluationMetrics).length > 0 ? (
                        <>
                            <Title level={5} style={{ marginBottom: 12 }}>Metrics by Agent</Title>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                                {Object.entries(agentEvaluationMetrics)
                                    .filter(([agent]) => targetAgentMatchesPrefixes(targetAgent, [agent]))
                                    .sort(([a], [b]) => compareAgents(a, b))
                                    .map(([agent, summary]) => {
                                    const knownTotal = summary.passed + summary.failed;
                                    const passPercentForAgent = knownTotal > 0 ? Math.round((summary.passed / knownTotal) * 100) : null;
                                    const averageScore = summary.scoreCount > 0 ? summary.scoreSum / summary.scoreCount : null;
                                    return (
                                        <Card
                                            key={agent}
                                            size="small"
                                            title={titleCase(agent)}
                                            style={{ minWidth: 260, flex: '1 1 260px' }}
                                        >
                                            <Space size={6} wrap style={{ marginBottom: 8 }}>
                                                <Tag color="success" style={{ margin: 0 }}>{summary.passed} passed</Tag>
                                                <Tag color="error" style={{ margin: 0 }}>{summary.failed} failed</Tag>
                                                {summary.unknown > 0 && <Tag style={{ margin: 0 }}>{summary.unknown} unknown</Tag>}
                                            </Space>
                                            {passPercentForAgent != null && (
                                                <div style={{ marginBottom: 8 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>Evaluator pass rate</Text>
                                                        <Text strong style={{ fontSize: 13 }}>{passPercentForAgent}%</Text>
                                                    </div>
                                                    <Progress
                                                        percent={passPercentForAgent}
                                                        size="small"
                                                        showInfo={false}
                                                        strokeColor={scoreColor(passPercentForAgent)}
                                                    />
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Evaluators</Text>
                                                <Text strong style={{ fontSize: 13 }}>{summary.evaluators}</Text>
                                            </div>
                                            {averageScore != null && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>Average score</Text>
                                                    <Text strong style={{ fontSize: 13 }}>{averageScore.toFixed(2)}</Text>
                                                </div>
                                            )}
                                        </Card>
                                    );
                                })}
                            </div>
                        </>
                    ) : metricsGrouped && Object.keys(metricsGrouped).length > 0 && (
                        <>
                            <Title level={5} style={{ marginBottom: 12 }}>Metrics by Agent</Title>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                                {(results.agents_evaluated ?? Object.keys(metricsGrouped))
                                    .filter((agent) => targetAgentMatchesPrefixes(targetAgent, [agent]))
                                    .sort(compareAgents)
                                    .map((agent) => {
                                    const agentMetrics = metricsGrouped[agent];
                                    if (!agentMetrics) return null;
                                    return (
                                        <Card
                                            key={agent}
                                            size="small"
                                            title={titleCase(agent)}
                                            style={{ minWidth: 260, flex: '1 1 260px' }}
                                        >
                                            {Object.entries(agentMetrics)
                                                .filter(([k]) => isDisplayMetric(k))
                                                .map(([k, v]) => {
                                                    const pct = typeof v === 'number' ? metricPercent(v) : null;
                                                    return (
                                                        <div key={k} style={{ marginBottom: 8 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    {humanizeMetricKey(k)}
                                                                </Text>
                                                                <Text strong style={{ fontSize: 13 }}>
                                                                    {typeof v === 'number' ? v.toFixed(2) : String(v)}
                                                                </Text>
                                                            </div>
                                                            {pct != null && (
                                                                <Progress
                                                                    percent={pct}
                                                                    size="small"
                                                                    showInfo={false}
                                                                    strokeColor={scoreColor(pct)}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </Card>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ── Consistency Analysis ── */}
                    {consistency && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <Title level={5} style={{ margin: 0 }}>Consistency Analysis</Title>
                                {consistency.llm_available != null && (
                                    <Tooltip title={consistency.llm_available
                                        ? 'LLM-based semantic scoring ran for this run'
                                        : 'LLM-based semantic scoring was unavailable; semantic sections may be empty'}>
                                        <Tag color={consistency.llm_available ? 'success' : 'default'}>
                                            {consistency.llm_available ? 'LLM scoring on' : 'LLM scoring off'}
                                        </Tag>
                                    </Tooltip>
                                )}
                            </div>
                            <Card size="small" style={{ marginBottom: 20 }}>
                                <Table
                                    size="small"
                                    pagination={false}
                                    rowKey="field"
                                    dataSource={Object.entries(consistency.aggregate)
                                        .sort(([a], [b]) => compareConsistencyFields(a, b))
                                        .map(([field, agg]) => ({
                                            field,
                                            mean: agg.mean_exact_match,
                                            min: agg.min_exact_match,
                                        }))}
                                    columns={[
                                        { title: 'Field', dataIndex: 'field', key: 'field', render: (v: string) => consistencyFieldLabel(v) },
                                        {
                                            title: 'Mean Exact Match',
                                            dataIndex: 'mean',
                                            key: 'mean',
                                            width: 280,
                                            render: (v: number) => {
                                                const pct = Math.round(v * 100);
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Progress
                                                            percent={pct}
                                                            size="small"
                                                            showInfo={false}
                                                            strokeColor={scoreColor(pct)}
                                                            style={{ flex: 1, margin: 0 }}
                                                        />
                                                        <Text strong style={{ fontSize: 12, minWidth: 36 }}>{pct}%</Text>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            title: 'Min Exact Match',
                                            dataIndex: 'min',
                                            key: 'min',
                                            width: 120,
                                            align: 'center',
                                            render: (v: number) => {
                                                const pct = Math.round(v * 100);
                                                return <Tag color={pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error'}>{pct}%</Tag>;
                                            },
                                        },
                                    ]}
                                />

                                {consistency.per_question.length > 0 && (
                                    <div style={{ marginTop: 16 }}>
                                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Per-Question Drilldown</Text>
                                        <Collapse
                                            size="small"
                                            items={consistency.per_question.map((pq, idx) => ({
                                                key: String(idx),
                                                label: (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Text ellipsis style={{ flex: 1 }}>{pq.query}</Text>
                                                        <Tag>{pq.runs} runs</Tag>
                                                        {Object.entries(pq.fields)
                                                            .sort(([a], [b]) => compareConsistencyFields(a, b))
                                                            .map(([f, d]) => (
                                                            <Tooltip key={f} title={`${consistencyFieldLabel(f)}: ${Math.round(d.exact_match_rate * 100)}%`}>
                                                                {d.exact_match_rate === 1.0
                                                                    ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                                    : d.exact_match_rate === 0.0
                                                                        ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                                        : <WarningOutlined style={{ color: '#faad14' }} />}
                                                            </Tooltip>
                                                        ))}
                                                    </div>
                                                ),
                                                children: (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                                                        {Object.entries(pq.fields)
                                                            .sort(([a], [b]) => compareConsistencyFields(a, b))
                                                            .map(([fieldName, detail]) => (
                                                            <div key={fieldName} style={{ minWidth: 0, maxWidth: '100%' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                                    <Text strong style={{ fontSize: 12 }}>{consistencyFieldLabel(fieldName)}</Text>
                                                                    <Tag color={detail.exact_match_rate === 1 ? 'success' : detail.exact_match_rate === 0 ? 'error' : 'warning'}>
                                                                        {Math.round(detail.exact_match_rate * 100)}% match
                                                                    </Tag>
                                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                                        {detail.unique_outputs} unique output{detail.unique_outputs !== 1 ? 's' : ''}
                                                                    </Text>
                                                                </div>
                                                                {Array.isArray(detail.outputs_per_run) && detail.outputs_per_run.length > 0 && (
                                                                    <Table
                                                                        size="small"
                                                                        pagination={false}
                                                                        rowKey="run_index"
                                                                        tableLayout="fixed"
                                                                        style={{ width: '100%' }}
                                                                        dataSource={[...detail.outputs_per_run].sort((a, b) => a.run_index - b.run_index)}
                                                                        columns={[
                                                                            { title: 'Run', dataIndex: 'run_index', key: 'run_index', width: 60, align: 'center' },
                                                                            {
                                                                                title: 'Output',
                                                                                dataIndex: 'output',
                                                                                key: 'output',
                                                                                render: (v: unknown) => (
                                                                                    <div style={{ fontSize: 12, maxWidth: '100%', maxHeight: 320, overflowX: 'auto', overflowY: 'auto' }}>
                                                                                        {renderConsistencyOutput(fieldName, v)}
                                                                                    </div>
                                                                                ),
                                                                            },
                                                                        ]}
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ),
                                            }))}
                                        />
                                    </div>
                                )}

                                {consistency.plan_structure?.aggregate && (
                                    <ConsistencyScoreTable title="Plan Structure Consistency" stats={consistency.plan_structure.aggregate} />
                                )}
                                {consistency.whole_response?.aggregate && (
                                    <ConsistencyScoreTable title="Whole-Response Consistency" stats={consistency.whole_response.aggregate} />
                                )}
                                {consistency.per_task && (
                                    <ConsistencyPerTaskPanel section={consistency.per_task} />
                                )}
                            </Card>
                        </>
                    )}

                    {/* ── C + D. Per-Question Results ── */}
                    {questionRows.length > 0 && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                                    <Title level={5} style={{ margin: 0 }}>Per-Question Results</Title>
                                    <Text type="secondary">
                                        {passCount} of {totalCount} passed
                                        {errorCount > 0 && `, ${errorCount} error${errorCount > 1 ? 's' : ''}`}
                                    </Text>
                                </div>
                                <Segmented
                                    size="small"
                                    value={questionFilter}
                                    onChange={(v) => setQuestionFilter(v as QuestionFilter)}
                                    options={[
                                        { label: `All (${totalCount})`, value: 'all' },
                                        { label: `Passed (${passCount})`, value: 'passed' },
                                        { label: `Failed (${failCount})`, value: 'failed' },
                                        ...(errorCount > 0
                                            ? [{ label: `Errors (${errorCount})`, value: 'errors' as const }]
                                            : []),
                                    ]}
                                />
                            </div>
                            <Table
                                rowKey="question_index"
                                dataSource={filteredRows}
                                columns={questionColumns}
                                size="small"
                                pagination={false}
                                scroll={{ x: 1120 }}
                                expandable={{ expandedRowRender: renderExpandedRow }}
                            />
                        </>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default SkillStudioEvalRunDetailDrawer;
